import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { fetchAuditLogs, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

const RESOURCES = ["", "menu_item", "category", "user", "settings", "file"];
const ACTIONS = ["", "create", "update", "delete", "reorder", "upload", "request_reset", "reset_password"];

function formatWhen(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch { return iso; }
}

function actionColor(action) {
  switch (action) {
    case "create": case "upload": return "bg-[#0F5F63] text-[#FDFBF7]";
    case "update": case "reorder": return "bg-[#F4F2EB] text-[#0F5F63]";
    case "delete": return "bg-[#E27B58] text-white";
    case "request_reset": case "reset_password": return "bg-amber-100 text-amber-800";
    default: return "bg-[#F4F2EB] text-[#5C6B6D]";
  }
}

export default function AuditLog() {
  const { lang, t } = useLang();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resType, setResType] = useState("");
  const [action, setAction] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (resType) params.resource_type = resType;
      if (action) params.action = action;
      const r = await fetchAuditLogs(params);
      setLogs(r.logs || []);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resType, action]);

  return (
    <div data-testid="audit-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          <Select value={resType || "all"} onValueChange={(v) => setResType(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-full w-44" data-testid="audit-resource-filter">
              <SelectValue placeholder={t.auditResource} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.filterAll} · {t.auditResource}</SelectItem>
              {RESOURCES.filter(Boolean).map((r) => (
                <SelectItem key={r} value={r}>{t[`resource_${r}`] || r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={action || "all"} onValueChange={(v) => setAction(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-full w-44" data-testid="audit-action-filter">
              <SelectValue placeholder={t.auditAction} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.filterAll} · {t.auditAction}</SelectItem>
              {ACTIONS.filter(Boolean).map((a) => (
                <SelectItem key={a} value={a}>{t[`action_${a}`] || a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} variant="outline" data-testid="audit-refresh-btn"
          className="rounded-full border-[#0F5F63]/20 text-[#0F5F63]">
          <RefreshCw className={`w-4 h-4 me-2 ${loading ? "animate-spin" : ""}`} /> {t.refresh || "Refresh"}
        </Button>
      </div>

      <div className="rounded-2xl bg-white border border-black/5 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#F4F2EB] text-xs uppercase tracking-widest text-[#5C6B6D]">
          <div className="col-span-3">{t.auditWhen}</div>
          <div className="col-span-2">{t.auditActor}</div>
          <div className="col-span-2">{t.auditAction}</div>
          <div className="col-span-1">{t.auditResource}</div>
          <div className="col-span-4">{t.auditSummary}</div>
        </div>
        {loading ? (
          <div className="p-8 text-sm text-[#5C6B6D] text-center">…</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-sm text-[#5C6B6D] text-center">{t.noAuditYet}</div>
        ) : (
          logs.map((l) => (
            <div key={l.id} className="grid grid-cols-12 gap-4 px-6 py-3 border-t border-black/5 items-center text-sm" data-testid={`audit-row-${l.id}`}>
              <div className="col-span-3 text-[#5C6B6D] text-xs">{formatWhen(l.created_at, lang)}</div>
              <div className="col-span-2">
                {l.actor_email ? (
                  <>
                    <p className="font-medium text-[#1A1A1A] truncate">{l.actor_email}</p>
                    <p className="text-[10px] uppercase tracking-widest text-[#5C6B6D]">{l.actor_role}</p>
                  </>
                ) : (
                  <span className="text-xs text-[#5C6B6D] italic">system</span>
                )}
              </div>
              <div className="col-span-2">
                <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${actionColor(l.action)}`}>
                  {t[`action_${l.action}`] || l.action}
                </span>
              </div>
              <div className="col-span-1 text-[#5C6B6D] text-xs">{t[`resource_${l.resource_type}`] || l.resource_type}</div>
              <div className="col-span-4 text-[#1A1A1A] truncate" title={l.summary}>{l.summary}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
